import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { IonButton, IonItem } from '@ionic/angular/standalone';
import { Payment } from 'src/app/services/payment.service';

declare const Stripe: any;
@Component({
  selector: 'app-student-payment-form',
  templateUrl: './student-payment-form.component.html',
  styleUrls: ['./student-payment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, IonItem, IonButton]
})
export class StudentPaymentFormComponent implements OnInit, OnDestroy {
  @Input() paymentKey: string;
  @Input() intentSecret: string;
  @Input() payment: Payment;
  @Output() paymentComplete = new EventEmitter<void>();
  @ViewChild('cardInfo', { static: true }) cardInfo: ElementRef;
  stripe: any;
  card: any;
  cardHandler = this.onChange.bind(this);
  error: string;
  inProgress = false;
  success = false;
  errorMessage: string;
  legend = 'Complete';
  ready = false;
  paymentForm = this.fb.group({});

  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.stripe = Stripe(this.paymentKey);
    this.setupStripe();
  }

  ngOnDestroy(): void {
    this.card.removeEventListener('change', this.cardHandler);
    this.card.destroy();
  }

  onChange({ error }): void {
    if (error) {
      this.error = error.message;
    } else {
      this.error = null;
    }
    this.cd.detectChanges();
  }

  setupStripe(): void {
    const elements = this.stripe.elements();
    const style = {
      base: {
        'color': '#32325d',
        'lineHeight': '24px',
        'fontFamily': '"Helvetica Neue", Helvetica, sans-serif',
        'fontSmoothing': 'antialiased',
        'fontSize': '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#e93423',
        iconColor: '#e93423'
      }
    };
    this.card = elements.create('card', { style });
    this.card.mount(this.cardInfo.nativeElement);

    this.card.addEventListener('change', this.cardHandler);

    const paymentRequest = this.stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: this.payment.title,
        amount: this.payment.amount
      },
      requestPayerName: true,
      requestPayerEmail: true
    });

    const prButton = elements.create('paymentRequestButton', {
      paymentRequest
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result) {
        prButton.mount('#payment-request-button');
        this.legend = 'Or enter';
      }
      this.ready = true;
      this.cd.detectChanges();
    });

    paymentRequest.on('paymentmethod', (ev) => {
      this.stripe
        .confirmCardPayment(this.intentSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false })
        .then((confirmResult) => {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            ev.complete('fail');
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            ev.complete('success');
            // Let Stripe.js handle the rest of the payment flow.
            this.stripe.confirmCardPayment(this.intentSecret).then((result) => {
              if (result.error) {
                // The payment failed -- ask your customer for a new payment method.
              } else {
                // The payment has succeeded.
                this.success = true;
                this.paymentComplete.emit();
              }
            });
          }
        });
    });
  }

  async onSubmit(): Promise<void> {
    if (this.paymentForm.valid) {
      this.inProgress = true;
      this.errorMessage = null;
      this.stripe
        .handleCardPayment(this.intentSecret, this.card, {
          save_payment_method: false
        })
        .then((result) => {
          if (result.error) {
            this.errorMessage = result.error.message;
            this.inProgress = false;
          } else {
            this.success = true;
            this.paymentComplete.emit();
          }
        });
    }
  }
}
